import React from 'react';
import {
  Typography,
  Container,
  Box,
  Button,
  Card,
  CardMedia,
  Chip,
  Divider,
} from '@mui/material';
import { Grid } from '@mui/material';
import { Star as StarIcon, Inventory } from '@mui/icons-material';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { useTheme } from '../contexts/ThemeContext';
import { TopBar } from '../layout/TopBar';
import { BottomNavigation } from '../layout/BottomNavigation';
import { useNavigate } from 'react-router-dom';

ChartJS.register(ArcElement, Tooltip, Legend);

const topItems = [
  { rank: 1, image: 'https://placehold.co/300x300/D8C8C3/333?text=Prato%20com%20Talheres' },
  { rank: 2, image: 'https://placehold.co/300x300/D32F2F/FFF?text=Guardanapo' },
  { rank: 3, image: 'https://placehold.co/300x300/F5E4C4/333?text=Refei%C3%A7%C3%A3o' },
  { rank: 4, image: 'https://placehold.co/300x300/1976D2/FFF?text=X%C3%ADcara' },
];

const FavoriteItemsPage: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();

  const chartConfig = {
    data: {
      datasets: [
        {
          data: [11.1, 11.1, 33.3, 11.1, 33.3],
          backgroundColor: [
            theme.colors.secondary,
            theme.colors.accent,
            theme.colors.primary,
            '#F5F5F5',
            '#8D8D8D'
          ],
          borderColor: theme.colors.background,
          borderWidth: 2,
          cutout: '70%',
        },
      ],
    },
    options: {
      responsive: true,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
      },
    },
  };

  return (
    <>
      <TopBar
        title="Itens Favoritos"
        icon={<Inventory sx={{ fontSize: '0.875rem' }} />}
        showBackButton={true}
        showProfileButton={true}
        onBackClick={() => navigate(-1)}
      />
      <Box sx={{
        paddingBottom: '120px',
        paddingTop: '70px',
        padding: 2,
        maxWidth: '400px',
        margin: '0 auto',
        minHeight: '100vh',
      }}>
        <Container sx={{ py: 2, px: 0 }}>
          {/* Período */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 2 
          }}>
            <Chip 
              label="FEV/25" 
              sx={{ 
                backgroundColor: theme.colors.secondary,
                color: theme.colors.text.primary,
                fontWeight: 'bold',
                fontSize: '0.75rem',
              }} 
            />
            <Button 
              variant="contained" 
              sx={{ 
                backgroundColor: theme.colors.secondary,
                color: theme.colors.text.primary,
                borderRadius: theme.radii.medium,
                fontSize: '0.75rem',
                fontWeight: 'bold',
                textTransform: 'none',
                padding: '6px 16px',
                '&:hover': {
                  backgroundColor: theme.colors.secondary,
                  opacity: 0.8,
                },
              }}
            >
              Selecionar período
            </Button>
          </Box>

          {/* Top 4 Itens */}
          <Typography 
            sx={{ 
              color: theme.colors.text.primary,
              fontWeight: 'bold',
              fontSize: '0.875rem',
              marginBottom: 1.5,
            }}
          >
            TOP 4 ITENS MAIS USADOS
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {topItems.map((item) => (
              <Grid key={item.rank} size={6}>
                <Card sx={{ 
                  borderRadius: theme.radii.large,
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: theme.shadows.small,
                }}>
                  <CardMedia
                    component="img"
                    image={item.image}
                    alt={`Item ${item.rank}`}
                    sx={{ aspectRatio: '1 / 1' }}
                  />
                  <Box sx={{ 
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    p: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}>
                    <Chip
                      icon={<StarIcon sx={{ color: '#FFC107 !important' }} />}
                      label={`${item.rank}º`}
                      sx={{ 
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        alignSelf: 'flex-start',
                        fontWeight: 'bold',
                        fontSize: '0.75rem',
                      }}
                    />
                    <Button 
                      variant="contained" 
                      sx={{ 
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        color: theme.colors.text.primary,
                        alignSelf: 'flex-start',
                        fontSize: '0.7rem',
                        fontWeight: 'bold',
                        padding: '4px 12px',
                        borderRadius: theme.radii.small,
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 1)',
                        },
                      }}
                    >
                      VER
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          <Divider sx={{ my: 3, borderColor: theme.colors.border }} />

          {/* Gráfico de Distribuição */}
          <Typography 
            sx={{ 
              color: theme.colors.text.primary,
              fontWeight: 'bold',
              fontSize: '0.875rem',
              marginBottom: 2,
              textAlign: 'center',
            }}
          >
            DISTRIBUIÇÃO POR CATEGORIA
          </Typography>

          <Box sx={{ 
            position: 'relative',
            width: '100%',
            maxWidth: 280,
            margin: 'auto',
            mb: 3,
          }}>
            <Doughnut data={chartConfig.data} options={chartConfig.options} />
            <Typography sx={{ 
              position: 'absolute',
              top: '10%',
              right: '-5%',
              fontSize: '0.7rem',
              fontWeight: 'bold',
              color: theme.colors.text.primary,
              textAlign: 'right',
            }}>
              Guardanapos<br />11.1%
            </Typography>
            <Typography sx={{ 
              position: 'absolute',
              top: '30%',
              right: '-10%',
              fontSize: '0.7rem',
              fontWeight: 'bold',
              color: theme.colors.text.primary,
              textAlign: 'right',
            }}>
              Pratos<br />33.3%
            </Typography>
            <Typography sx={{ 
              position: 'absolute',
              bottom: '30%',
              right: '-5%',
              fontSize: '0.7rem',
              fontWeight: 'bold',
              color: theme.colors.text.primary,
              textAlign: 'right',
            }}>
              Talheres<br />11.1%
            </Typography>
            <Typography sx={{ 
              position: 'absolute',
              bottom: '10%',
              left: '15%',
              fontSize: '0.7rem',
              fontWeight: 'bold',
              color: theme.colors.text.primary,
              textAlign: 'left',
            }}>
              Jogo americano<br />33.3%
            </Typography>
            <Typography sx={{ 
              position: 'absolute',
              top: '30%',
              left: '-15%',
              fontSize: '0.7rem',
              fontWeight: 'bold',
              color: theme.colors.text.primary,
              textAlign: 'left',
            }}>
              Taças<br />11.1%
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button 
              variant="contained" 
              sx={{ 
                backgroundColor: theme.colors.secondary,
                color: theme.colors.text.primary,
                borderRadius: theme.radii.medium,
                fontSize: '0.875rem',
                fontWeight: 'bold',
                padding: '8px 24px',
                '&:hover': {
                  backgroundColor: theme.colors.secondary,
                  opacity: 0.8,
                },
              }}
            >
              VER DETALHES
            </Button>
          </Box>
        </Container>

        {/* Bottom Navigation */}
        <BottomNavigation />
      </Box>
    </>
  );
};

export default FavoriteItemsPage;

